import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, CalendarDays, MessageSquare, MoreHorizontal } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import {
  Megaphone, FileText, Church, GraduationCap, DollarSign,
  Heart, Settings, LogOut, CreditCard, LifeBuoy, ShieldCheck, HandHeart,
  Users2, Flag, Wallet, Sprout, Lock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useEntitlements } from '../../context/EntitlementsContext';

export function BottomNav() {
  const location = useLocation();
  const { logout, canRead } = useAuth();
  const { hasModule } = useEntitlements();

  const mainTabs = [
    { to: '/', icon: LayoutDashboard, label: 'Inicio', module: 'dashboard' },
    { to: '/members', icon: Users, label: 'Miembros', module: 'members' },
    { to: '/events', icon: CalendarDays, label: 'Eventos', module: 'events' },
    { to: '/petitions', icon: MessageSquare, label: 'Bandeja', module: 'petitions' },
  ].filter(tab => canRead(tab.module));

  const moreItems = [
    { to: '/announcements', icon: Megaphone, label: 'Anuncios', module: 'announcements' },
    { to: '/posts', icon: FileText, label: 'Noticias', module: 'posts' },
    { to: '/ministries', icon: Church, label: 'Ministerios', module: 'ministries' },
    { to: '/groups', icon: Users2, label: 'Grupos', module: 'groups' },
    { to: '/courses', icon: GraduationCap, label: 'Cursos', module: 'courses' },
    { to: '/contributions', icon: DollarSign, label: 'Aportes', module: 'contributions' },
    { to: '/wallet', icon: Wallet, label: 'Wallet', module: 'wallet', addon: 'wallet_dispersion' },
    { to: '/prayers', icon: Heart, label: 'Oraciones', module: 'prayers' },
    { to: '/mentorships', icon: Sprout, label: 'Mentorías', module: 'mentorships' },
    { to: '/moderation', icon: Flag, label: 'Moderación', module: 'moderation' },
    { to: '/settings', icon: Settings, label: 'Configuración', module: 'settings' },
    { to: '/team', icon: ShieldCheck, label: 'Equipo', module: 'team' },
    { to: '/apoyo-pastoral', icon: HandHeart, label: 'Tu Aliado Pastoral', module: 'apoyo_pastoral', addon: 'aliado_pastoral' },
    { to: '/subscriptions', icon: CreditCard, label: 'Suscripciones', module: 'subscriptions' },
    { to: '/support', icon: LifeBuoy, label: 'Soporte', module: 'support' },
  ].filter(item => canRead(item.module));

  const isMoreActive = moreItems.some(item => location.pathname === item.to || location.pathname.startsWith(item.to));

  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t" data-testid="bottom-nav">
      <div className="flex items-center justify-around h-16 px-2 pb-[env(safe-area-inset-bottom)]">
        {mainTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = location.pathname === tab.to || (tab.to !== '/' && location.pathname.startsWith(tab.to));
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              data-testid={`bottomnav-${tab.label.toLowerCase()}`}
              className={`bottom-nav-item flex flex-col items-center gap-0.5 py-1 px-3 text-[11px] transition-colors ${
                isActive ? 'active text-[#056A84] font-medium' : 'text-muted-foreground'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span>{tab.label}</span>
              {isActive && <div className="nav-indicator" />}
            </NavLink>
          );
        })}
        {moreItems.length > 0 && (
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="bottomnav-more"
                className={`bottom-nav-item flex flex-col items-center gap-0.5 py-1 px-3 text-[11px] transition-colors ${
                  isMoreActive ? 'active text-[#056A84] font-medium' : 'text-muted-foreground'
                }`}
              >
                <MoreHorizontal className="w-5 h-5" />
                <span>Más</span>
                {isMoreActive && <div className="nav-indicator" />}
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl max-h-[70vh]">
              <SheetHeader>
                <SheetTitle>Más opciones</SheetTitle>
              </SheetHeader>
              <div className="py-4 space-y-1">
                {moreItems.map((item) => {
                  const Icon = item.icon;
                  const locked = item.addon && !hasModule(item.addon);
                  const isActive = location.pathname === item.to;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                        isActive ? 'bg-accent text-accent-foreground font-medium' : 'text-foreground hover:bg-accent/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className={locked ? 'text-muted-foreground' : ''}>{item.label}</span>
                      {locked && <Lock className="w-3.5 h-3.5 ml-auto text-[#C58B2A]" />}
                    </NavLink>
                  );
                })}
                <button
                  onClick={logout}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-destructive hover:bg-destructive/10 w-full"
                  data-testid="mobile-logout"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </div>
  );
}

export default BottomNav;
